// 事務所ごとに変えられる数値と機能（仕様書「設定画面からの数値の変更、機能の ON / OFF」）。
// 法令で決まった値（休息 9 時間・連続運転 4 時間・拘束時間の上限）は変えられない。ここに載せるのは事務所の運用で決める値だけ
import { z } from 'zod';
import { RULES } from './rules';

export const SETTING_DEFS = [
  { key: 'unacceptedAlertMin', label: '依頼を受けないまま警告するまで', unit: '分', min: 1, max: 30, def: RULES.unacceptedAlertMin },
  { key: 'unassignedWarnMin', label: '未割り当ての配送を注意するまで', unit: '分', min: 5, max: 120, def: RULES.unassignedWarnMin },
  { key: 'unassignedAlertMin', label: '未割り当ての配送を警告するまで', unit: '分', min: 5, max: 240, def: RULES.unassignedAlertMin },
  { key: 'rollCallWaitingAlertMin', label: '点呼の確認待ちを警告にするまで', unit: '分', min: 1, max: 60, def: RULES.rollCallWaitingAlertMin },
  { key: 'unknownStopAlertMin', label: '理由不明の停車を警告するまで', unit: '分', min: 5, max: 60, def: RULES.unknownStopAlertMin },
  { key: 'noSignalAlertMin', label: '位置が届かないとき警告するまで', unit: '分', min: 5, max: 60, def: RULES.noSignalAlertMin },
  { key: 'chatUnreadAlertMin', label: 'ドライバーのメッセージが読まれないとき警告するまで', unit: '分', min: 1, max: 60, def: RULES.chatUnreadAlertMin },
] as const;
export const FEATURE_DEFS = [
  { key: 'chat', label: 'チャット', note: '止めると、事務所とアプリのチャットの画面を出さず、新しいメッセージを受け付けません' },
  { key: 'routeRecommend', label: '回る順番のおすすめ', note: '止めると、配送指示・順番の画面とアプリにおすすめを出しません' },
  { key: 'driverReorder', label: 'ドライバーによる順番の変更', note: '止めると、配送アプリの「順番を変える」を出しません' },
] as const;
export type SettingKey = (typeof SETTING_DEFS)[number]['key'];
export type FeatureKey = (typeof FEATURE_DEFS)[number]['key'];
export type OfficeSettings = { values: Record<SettingKey, number>; features: Record<FeatureKey, boolean>; version: number };

export const DEFAULT_SETTINGS: OfficeSettings = {
  values: Object.fromEntries(SETTING_DEFS.map((d) => [d.key, d.def])) as Record<SettingKey, number>,
  features: Object.fromEntries(FEATURE_DEFS.map((d) => [d.key, true])) as Record<FeatureKey, boolean>,
  version: 1,
};

export const SettingsUpdate = z.strictObject({
  values: z.strictObject(Object.fromEntries(SETTING_DEFS.map((d) => [d.key, z.number().int(`${d.label}は整数で入れてください`).min(d.min, `${d.label}は ${d.min}${d.unit}以上です`).max(d.max, `${d.label}は ${d.max}${d.unit}までです`)])) as Record<SettingKey, z.ZodNumber>),
  features: z.strictObject(Object.fromEntries(FEATURE_DEFS.map((d) => [d.key, z.boolean()])) as Record<FeatureKey, z.ZodBoolean>),
}).refine((s) => s.values.unassignedAlertMin > s.values.unassignedWarnMin, { message: '未割り当ての警告は、注意より長くしてください', path: ['values', 'unassignedAlertMin'] });
export type SettingsUpdate = z.infer<typeof SettingsUpdate>;

/** 保存した値に足りない項目があれば既定値で埋める（項目を足したときのため） */
export function mergeSettings(saved: { values?: Partial<Record<string, number>>; features?: Partial<Record<string, boolean>> } | null, version = 1): OfficeSettings {
  const values = { ...DEFAULT_SETTINGS.values };
  const features = { ...DEFAULT_SETTINGS.features };
  for (const d of SETTING_DEFS) { const v = saved?.values?.[d.key]; if (typeof v === 'number' && v >= d.min && v <= d.max) values[d.key] = v; }
  for (const d of FEATURE_DEFS) { const v = saved?.features?.[d.key]; if (typeof v === 'boolean') features[d.key] = v; }
  return { values, features, version };
}
